/**
 * canvasRecipes — curated multi-node insertion templates for the Task Picker.
 *
 * Spec: agentlets-production/CANVAS_PICKER_INLINE_DESIGN.md §2.4.2 + §5.
 *
 * A recipe is a small DAG (2-5 nodes) that drops onto a single edge, replacing
 * the original `from → to` connection with `from → entry … exit → to`. The
 * picker shows recipes prominently in the For-you tab so common micro-flows
 * become a one-click action — this is the headline differentiator vs. Conductor OSS.
 *
 * Recipe shape:
 *   {
 *     id: stable-key,
 *     name: human-readable,
 *     description: one-sentence purpose,
 *     appliesTo: ['edge'] (only edge insertions for v1),
 *     nodes: [{ id, type, label, config? }, ...],
 *     edges: [{ from: nodeId, to: nodeId }, ...]   // internal-only edges
 *     entry: id of the node that connects FROM the original edge's source
 *     exit:  id of the node that connects TO   the original edge's target
 *     source: 'curated' | 'user'                  // 'user' for saved recipes
 *   }
 *
 * Adding a recipe later: append to RECIPES below. The Task Picker loads
 * `getCuratedRecipes()` once on mount, so a hot-reload picks up new rows
 * without any other plumbing.
 *
 * User-saved recipes (Phase 4 stretch goal) live alongside these in
 * localStorage under `canvas.userRecipes` with `source: 'user'`. They appear
 * above curated rows in the picker.
 */

/** @type {object[]} */
const RECIPES = [
  {
    id: 'validate-and-approve',
    name: 'Validate & approve',
    description: 'Adds a policy gate, output validator, and approval gate.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'policy_enforcement_gate', label: 'Policy enforcement gate' },
      { id: 'r2', type: 'policy_rule_evaluation', label: 'Policy rule evaluation' },
      { id: 'r3', type: 'human_task',       label: 'Approval gate' },
    ],
    edges: [
      { from: 'r1', to: 'r2' },
      { from: 'r2', to: 'r3' },
    ],
    entry: 'r1',
    exit:  'r3',
    source: 'curated',
  },

  {
    id: 'llm-with-guardrails',
    name: 'LLM with guardrails',
    description: 'Pre-call policy gate + LLM + post-call output validator.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'policy_enforcement_gate', label: 'Policy enforcement gate' },
      { id: 'r2', type: 'llm_task',         label: 'LLM task' },
      { id: 'r3', type: 'policy_rule_evaluation', label: 'Output validator' },
    ],
    edges: [
      { from: 'r1', to: 'r2' },
      { from: 'r2', to: 'r3' },
    ],
    entry: 'r1',
    exit:  'r3',
    source: 'curated',
  },

  {
    id: 'branch-by-severity',
    name: 'Branch by severity',
    description: 'Switch on a severity field with high / medium / low branches.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'switch_case',    label: 'Severity switch' },
      { id: 'r2', type: 'terminate', label: 'High severity end' },
      { id: 'r3', type: 'terminate', label: 'Medium severity end' },
      { id: 'r4', type: 'terminate', label: 'Low severity end' },
    ],
    // Switch outgoing ports map to case labels via fromPort. The recipe
    // applier patches the port names verbatim.
    edges: [
      { from: 'r1', to: 'r2', fromPort: 'high' },
      { from: 'r1', to: 'r3', fromPort: 'medium' },
      { from: 'r1', to: 'r4', fromPort: 'low' },
    ],
    entry: 'r1',
    // Branch recipes don't have a single exit — pick the first terminator
    // so the apply step still produces a topologically valid graph; the
    // user wires the rest themselves if they don't want the default ends.
    exit: 'r2',
    source: 'curated',
  },

  {
    id: 'connector-with-retry',
    name: 'Connector with retry',
    description: 'Connector call, then a condition that loops back on error.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'connector_task', label: 'Connector call' },
      { id: 'r2', type: 'condition',      label: 'Error?' },
    ],
    edges: [
      { from: 'r1', to: 'r2' },
    ],
    entry: 'r1',
    exit:  'r2',
    source: 'curated',
  },

  {
    id: 'ai-then-notify',
    name: 'AI then notify',
    description: 'LLM task pushing its result into a notification.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'llm_task',          label: 'LLM analysis' },
      { id: 'r2', type: 'notification_task', label: 'Notify team' },
    ],
    edges: [
      { from: 'r1', to: 'r2' },
    ],
    entry: 'r1',
    exit:  'r2',
    source: 'curated',
  },

  {
    id: 'audit-then-execute',
    name: 'Audit then execute',
    description: 'Records an audit_log entry before any downstream action runs.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'audit_record', label: 'Audit log' },
    ],
    edges: [],
    entry: 'r1',
    exit:  'r1',
    source: 'curated',
  },

  // Phishing-specific extra recipe (the +1 from the user's brief).
  {
    id: 'phishing-quarantine',
    name: 'Quarantine phishing email',
    description: 'IOC enrichment, severity classification, then quarantine on critical.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'connector_task', label: 'Enrich IOCs',   config: { connector: 'virustotal' } },
      { id: 'r2', type: 'llm_task',       label: 'Classify severity' },
      { id: 'r3', type: 'switch_case',         label: 'Severity switch' },
      { id: 'r4', type: 'connector_task', label: 'Quarantine email', config: { connector: 'email-security', action: 'quarantine-message' } },
    ],
    edges: [
      { from: 'r1', to: 'r2' },
      { from: 'r2', to: 'r3' },
      { from: 'r3', to: 'r4', fromPort: 'critical' },
    ],
    entry: 'r1',
    exit:  'r4',
    source: 'curated',
  },

  /* ────────────────────────────────────────────────────────────────────
   * cicd-deployment-gate — flagship DevOps recipe
   *
   * Demonstrates the canonical CI/CD policy-gate workflow shape that
   * the cicd_deployment_gate.rego WASM-OPA policy was designed for.
   * Drop this on the edge between any "build/scan completed" trigger
   * and your deploy task to instantly get:
   *
   *   image-scan results → policy decision → conditional approval →
   *   deploy → notify
   *
   * The middle node (`policy_gate`, config bound to cicd_deployment_gate)
   * runs the Rego against the canonical CI/CD input bundle. The switch
   * routes BLOCK verdicts to a terminate, WARN verdicts through an
   * approval gate, ALLOW verdicts straight to deploy. Notification on
   * any verdict so the deploying engineer always sees outcome.
   *
   * See backend/src/main/resources/policies/cicd_deployment_gate/SKILL.md
   * for the policy's full contract and rule inventory.
   * ──────────────────────────────────────────────────────────────────── */
  {
    id: 'cicd-deployment-gate',
    name: 'CI/CD deployment gate',
    description: 'Image scan → WASM-OPA policy → conditional approval → deploy. The canonical DevOps gating flow.',
    appliesTo: ['edge'],
    nodes: [
      { id: 'r1', type: 'connector_task',  label: 'Scan image',
        config: { connector: 'image-scanner', action: 'scan-and-attest' } },
      { id: 'r2', type: 'policy_enforcement_gate',     label: 'CI/CD policy',
        config: { policyName: 'cicd_deployment_gate', enforcementPoint: 'WORKFLOW_NODE' } },
      { id: 'r3', type: 'switch_case',          label: 'Verdict switch',
        config: { expression: 'input.policy.verdict' } },
      { id: 'r4', type: 'human_task',      label: 'Approval gate',
        config: { reason: 'Policy emitted WARN — human ack required before deploy proceeds' } },
      { id: 'r5', type: 'connector_task',  label: 'Deploy',
        config: { connector: 'kubernetes', action: 'apply-manifest' } },
      { id: 'r6', type: 'notification_task', label: 'Notify deployer',
        config: { channel: 'slack', recipient: '{{deploy.deployer.email}}' } },
      { id: 'r7', type: 'terminate',       label: 'Halt — blocked',
        config: { status: 'FAILED', message: 'Deployment blocked by cicd_deployment_gate policy. See verdict reasons in run audit.' } },
    ],
    edges: [
      { from: 'r1', to: 'r2' },
      { from: 'r2', to: 'r3' },
      { from: 'r3', to: 'r4', fromPort: 'WARN' },     /* policy warned → approval */
      { from: 'r3', to: 'r5', fromPort: 'ALLOW' },    /* clean pass → deploy */
      { from: 'r3', to: 'r7', fromPort: 'BLOCK' },    /* policy denied → halt */
      { from: 'r4', to: 'r5' },                        /* approval granted → deploy */
      { from: 'r5', to: 'r6' },                        /* deploy done → notify */
    ],
    entry: 'r1',
    exit:  'r6',
    source: 'curated',
  },
];

/**
 * Return the curated recipe library (immutable). Callers should treat the
 * result as read-only — the picker re-uses the same array reference, so
 * mutating it would silently affect every render.
 *
 * @returns {object[]}
 */
export function getCuratedRecipes() {
  return RECIPES;
}

/**
 * Lookup a single recipe by id. Returns null if unknown.
 *
 * @param {string} id
 * @returns {object|null}
 */
export function getRecipeById(id) {
  return RECIPES.find((r) => r.id === id) || null;
}
